'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { navData } from '@/utils/navData';
import LegalFooter from './LegalFooter/LegalFooter';
import SvgLogo from '@/assets/svg/clayton-built-logo-white.svg';
import SvgChevron from '@/assets/svg/icon-chevron.svg';
import SvgFacebook from '@/assets/svg/social/facebook.svg';
import SvgInstagram from '@/assets/svg/social/instagram.svg';
import SvgPinterest from '@/assets/svg/social/pinterest.svg';
import SvgYouTube from '@/assets/svg/social/youtube.svg';
import SvgTikTok from '@/assets/svg/social/tiktok.svg';

const Footer = () => {
  const [sectionState, setSectionState] = useState({
    ourStory: false,
    ourStandards: false,
    ourHomes: false,
    ourPartners: false,
    about: false,
  });

  const socialLinks = [
    {
      url: 'https://www.facebook.com/ClaytonHomes',
      icon: <SvgFacebook className="h-6 w-6 *:fill-white" />,
    },
    {
      url: 'https://www.instagram.com/clayton/',
      icon: (
        <SvgInstagram className="h-6 w-6 scale-150 pl-1 pt-[4.5px] *:fill-white" />
      ),
    },
    {
      url: 'https://www.pinterest.com/claytonhomes/',
      icon: (
        <SvgPinterest className="h-6 w-6 scale-150 pl-1 pt-[4.5px] *:fill-white" />
      ),
    },
    {
      url: 'https://www.youtube.com/user/claytonhomes',
      icon: (
        <SvgYouTube className="h-6 w-6 scale-150 pl-1 pt-[4.5px] *:fill-white" />
      ),
    },
    {
      url: 'https://www.tiktok.com/@claytonhomes',
      icon: <SvgTikTok className="h-6 w-6 *:fill-white" />,
    },
  ];

  const composeMenuSection = ({ sectionKey, nav, sectionClassName }) => {
    return (
      <div className={sectionClassName}>
        <h5
          onClick={() =>
            setSectionState({
              ...sectionState,
              [sectionKey]: !sectionState[sectionKey],
            })
          }
          className="border-navy-3 mb-8 flex justify-between border-b py-2.5 font-acumin text-[16px] font-bold leading-[1.25] tracking-[-0.3px] text-white md:mb-2.5"
        >
          {nav.title || 'Untitled'}
          <SvgChevron
            className={
              sectionState[sectionKey]
                ? 'rotate-180 *:fill-white md:hidden'
                : '*:fill-white md:hidden'
            }
          />
        </h5>
        <ul
          className={
            sectionState[sectionKey]
              ? 'mb-[50px] mt-[-20px] list-none md:my-0 md:block'
              : 'hidden md:my-0 md:block'
          }
        >
          {nav.items?.map(
            (item, index) =>
              item.isFooter && (
                <li
                  key={index}
                  className="mb-2 font-source-sans text-[16px] leading-[100%]"
                >
                  <Link
                    href={item.url}
                    target={item.openNewWindow ? '_blank' : '_self'}
                    rel={item.openNewWindow ? 'noopener' : ''}
                    className="text-grey-3 font-source-sans text-[14px] leading-[1.57] no-underline hover:text-white"
                  >
                    {item.title}
                  </Link>
                </li>
              )
          )}
        </ul>
      </div>
    );
  };

  return (
    <div className="bg-navy-1">
      <footer className="mx-auto my-0 w-[90%] max-w-[512px] pt-10 md:mb-5 md:flex md:w-full md:max-w-standard">
        <div className="md:mx-4 md:grid md:w-full md:grid-cols-[auto] md:grid-rows-[auto] md:gap-[5px]">
          {composeMenuSection({
            sectionKey: 'ourStory',
            nav: navData.ourStory,
            sectionClassName:
              'md:row-start-1 md:col-start-2 md:row-end-auto md:col-end-auto md:w-mobile',
          })}

          {composeMenuSection({
            sectionKey: 'ourStandards',
            nav: navData.ourStandards,
            sectionClassName: 'md:w-mobile md:row-start-1 md:col-start-3',
          })}

          {composeMenuSection({
            sectionKey: 'ourHomes',
            nav: navData.ourHomes,
            sectionClassName: 'md:row-start-2 md:col-start-2 md:w-mobile',
          })}

          {composeMenuSection({
            sectionKey: 'ourPartners',
            nav: navData.ourBrandPartners,
            sectionClassName:
              'md:ml-[-5px] md:row-start-1 md:row-end-[last-line] md:col-start-4 md:col-end-4 md:w-[70%]',
          })}

          {composeMenuSection({
            sectionKey: 'aboutUs',
            nav: navData.about,
            sectionClassName: 'md:w-mobile md:row-start-2 md:col-start-3',
          })}

          <div className="mt-[70px] text-center md:col-start-auto md:col-end-auto md:row-start-1 md:row-end-auto md:ml-0 md:mr-[150px] md:mt-0 md:w-[70%] md:pt-2.5 md:text-left">
            <div className="mb-[30px] flex justify-center md:ml-2.5 md:justify-normal">
              <SvgLogo alt="Clayton Built logo" data-testid="cb-logo-header" />
            </div>
            <ul className="mx-auto my-0 mb-6 flex max-w-[200px] justify-between md:my-0 md:ml-[2px] md:mr-0 md:max-w-none md:justify-normal md:gap-2.5">
              {socialLinks.map((item, index) => (
                <li
                  key={index}
                  className="font-source-sans text-[16px] leading-[1.69]"
                >
                  <Link
                    href={item.url}
                    target="_blank"
                    rel="noopener"
                    className="caption"
                  >
                    {item.icon}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </footer>

      <LegalFooter />
    </div>
  );
};

export default Footer;
